import type { ReactNode } from 'react'
import type { FaqItem } from '@/constants/reserve'

import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import TransitionLink from '@/components/generals/transition-link'

interface FaqProps {
    items: Array<FaqItem>
}

const Faq = ({ items }: FaqProps): ReactNode => {
    const [open, setOpen] = useState<Array<number>>([])

    const toggle = (index: number) => {
        setOpen(current => (current.includes(index) ? current.filter(item => item !== index) : [...current, index]))
    }

    return (
        <div className='border-t border-ivory/10'>
            {items.map((item, index) => {
                const expanded = open.includes(index)

                return (
                    <div key={item.question} className='border-b border-ivory/10'>
                        <h3>
                            <button
                                type='button'
                                id={`faq-question-${index}`}
                                aria-expanded={expanded}
                                aria-controls={`faq-answer-${index}`}
                                onClick={() => toggle(index)}
                                className='group flex w-full items-center justify-between gap-6 py-6 text-left md:py-8'
                            >
                                <span
                                    className={cn(
                                        'font-heading text-[1.45rem] leading-snug tracking-[-0.01em] transition-colors duration-500 md:text-[1.9rem]',
                                        expanded ? 'italic text-brass-bright' : 'text-ivory group-hover:text-brass-bright'
                                    )}
                                >
                                    {item.question}
                                </span>

                                <span
                                    aria-hidden='true'
                                    className={cn(
                                        'relative flex size-11 shrink-0 items-center justify-center rounded-full border transition-colors duration-500',
                                        expanded ? 'border-brass/70' : 'border-ivory/20 group-hover:border-ivory/45'
                                    )}
                                >
                                    <span className='absolute h-px w-3.5 bg-ivory/80' />
                                    <span
                                        className={cn(
                                            'absolute h-3.5 w-px bg-ivory/80 transition-transform duration-500 ease-film',
                                            expanded ? 'rotate-90 scale-y-0' : 'rotate-0'
                                        )}
                                    />
                                </span>
                            </button>
                        </h3>

                        <div
                            id={`faq-answer-${index}`}
                            role='region'
                            aria-labelledby={`faq-question-${index}`}
                            inert={!expanded}
                            className={cn(
                                'grid transition-[grid-template-rows,opacity] duration-700 ease-film',
                                expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                            )}
                        >
                            <div className='overflow-hidden'>
                                <div className='max-w-2xl pb-8 md:pb-10'>
                                    <p className='text-[1.05rem] leading-relaxed text-ivory/65'>
                                        {item.answer}
                                    </p>

                                    {item.link && (
                                        <TransitionLink href={item.link.href} className='link-line caps mt-6 text-ivory/80'>
                                            {item.link.label}
                                            <ArrowRight aria-hidden='true' className='size-4' strokeWidth={1.25} />
                                        </TransitionLink>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default Faq
